import React, { ReactElement, useEffect, useState } from 'react';
import Scroll, { scroller } from 'react-scroll';
import styled, { useTheme } from 'styled-components';

import { createMarkup, stripTags, timeMask } from 'app/utils';
import { TimelinePointState } from './types';
import { SendFeedbackProps, useContents } from 'features/contents';
// import { useModules } from 'features/modules';
// import { maskTeachers } from 'features/teachers';
import { useFullscreen } from 'features/fullscreen';

import { Box, ButtonIcon, Flex, RichContent, Typography } from 'components';
import { DownloadsBoxContent } from 'features/downloads';
import { useSchool } from 'features/school';

const { Element } = Scroll;

const CustomElement = styled(Element)`
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Bullet = styled.div`
  width: 26px;
  height: 26px;
  position: absolute;
  left: 18px;
  border-radius: 50%;
  cursor: ${(props: any) => (props['data-link'] ? 'pointer' : 'inherit')};

  &::before {
    content: '';
    display: block;
    width: 14px;
    height: 14px;

    border-radius: 50%;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  ${(props: any) => {
    return `
      top: 19px;

      ${props.theme.mediaQueries.desktop} {
        top: 27px;
      }
    `;
  }}
`;

const Square = styled.div`
  width: 18px;
  height: 18px;
  position: absolute;
  left: 22px;

  ${(props: any) => {
    return `
      top: 20px;

      ${props.theme.mediaQueries.desktop} {
        top: 32px;
      }
    `;
  }}
`;

const InteractionItem = styled(Element)`
  background: ${(props: any) => (props['data-active'] ? props.theme.colors.videoTimelineActiveBg : props.theme.colors.videoTimelineBg)};
  position: relative;

  ${Bullet} {
    background: ${(props: any) => (props['data-active'] ? props.theme.colors.videoTimelineActiveBg : props.theme.colors.videoTimelineBg)};

    &::before {
      border: 2px solid ${(props: any) => (props['data-active'] ? props.theme.colors.primary_400 : props.theme.colors.gray_600)};
      background: ${(props: any) =>
        props['data-active']
          ? props.theme.colors.primary_400
          : props['data-resolved']
          ? props.theme.colors.gray_600
          : props.theme.colors.videoTimelineBg};
    }
  }

  &::before {
    content: '';
    display: block;
    width: 2px;
    background: ${({ theme }) => theme.colors.gray_600};
    position: absolute;
    top: 0;
    left: 30px;
  }

  ${(props: any) => {
    return `
      padding: 20px 20px ${props['data-first'] ? '50px' : '20px'} 60px;

      &::before {
        height: ${props['data-first'] ? 'calc(100% - 30px)' : props['data-last'] ? '30px' : '100%'};
        margin-top: ${props['data-first'] ? '30px' : '0'};
      }

      ${props.theme.mediaQueries.desktop} {
        padding: 30px 30px ${props['data-first'] ? '80px' : '30px'} 60px;

        &::before {
          height: ${props['data-first'] ? 'calc(100% - 55px)' : props['data-last'] ? '30px' : '100%'};
          margin-top: ${props['data-first'] ? '55px' : '0'};
        }
      }
    `;
  }}
`;

const ElementWrapper = styled('div')`
  ${(props: any) => {
    return `
      width: 100vw;
      height: ${
        props['data-orientation'] === 'landscape' ? `calc(100vh - ${props['data-videoheight'] + (props['data-hastabs'] ? 167 : 122)}px)` : 'auto'
      };

      ${props.theme.mediaQueries.desktop} {
        width: 425px;
        height: ${
          props['data-hastabs'] ? 'calc(100vh - 81px - 60px)' : 'calc(100vh - 81px)'

          // props['data-hastabs']
          //   ? props['data-fullscreen']
          //     ? 'calc(100vh - 141px)'
          //     : 'calc(100vh - 81px - 60px)'
          //   : props['data-fullscreen']
          //   ? 'calc(100vh - 81px)'
          //   : '705px'
        };
      }
    `;
  }}
`;

interface TimelineProps {
  timelinePoint: TimelinePointState;
  playerGoTo: (seconds: number) => void;
  sendFeedback: (props: SendFeedbackProps) => void;
  hasTabs: boolean;
  orientation: 'portrait' | 'landscape';
  windowSize: {
    innerWidth: number;
    innerHeight: number;
    videoWidth: number;
    videoHeight: number;
  };
}

const Timeline = ({ timelinePoint, playerGoTo, sendFeedback, hasTabs, windowSize, orientation }: TimelineProps): ReactElement => {
  const { currentTheme, colors } = useTheme();
  const { schoolAlias } = useSchool();

  const [contentLiked, setContentLiked] = useState<null | boolean>(null);
  const [currentTimelinePoint, setCurrentTimelinePoint] = useState(timelinePoint.seconds || 0);

  const { contentDetails } = useContents();
  // const { moduleDetails } = useModules();
  const [isFullscreen] = useFullscreen();

  const goTo = (seconds: number) => {
    if (seconds) {
      scroller.scrollTo(`point-${seconds}`, {
        containerId: 'nestedContainerElement',
        spy: true,
        smooth: true,
        duration: 250,
      });
    }
  };

  // const timelineActions =
  //   contentDetails && contentDetails.video && contentDetails.video.timeline
  //     ? contentDetails.video.timeline.reduce((prev, timeline) => {
  //         return { ...prev };
  //       }, {})
  //     : null;

  // // console.log('timelineActions', timelineActions);

  useEffect(() => {
    setCurrentTimelinePoint(timelinePoint.seconds);
  }, [timelinePoint.seconds]);

  useEffect(() => {
    goTo(currentTimelinePoint);
  }, [currentTimelinePoint]);

  useEffect(() => {
    if (contentLiked !== null) {
      sendFeedback({
        liked: contentLiked,
      });
    }
  }, [contentLiked]);

  useEffect(() => {
    if (contentDetails.assessment.isComplete) {
      setContentLiked(contentDetails.assessment.liked);
    }
  }, [contentDetails]);

  if (!(contentDetails && contentDetails.video)) return <></>;

  const productClassTimelineName =
    contentDetails?.productClassTimelineName ||
    `${contentDetails?.timelineOrder} / ${contentDetails?.timelineCount} • ${contentDetails?.productName}`;

  return (
    <ElementWrapper data-fullscreen={isFullscreen} data-hastabs={hasTabs} data-videoheight={windowSize.videoHeight} data-orientation={orientation}>
      <CustomElement
        name="nestedContainer"
        className="element fixed-size"
        id="nestedContainerElement"
        style={{
          width: '100%',
          height: '100%',
          overflow: 'auto',
        }}
      >
        <InteractionItem data-first="true" name="point-0">
          <Square
            style={{ background: schoolAlias === 'b3' ? colors.dark_150 : colors.primary_400, borderRadius: schoolAlias === 'b3' ? '100px' : '0' }}
          />

          <Flex flexDirection="column">
            <Typography type="type3" upper color="color80" lineHeight="1.3" mb={{ m: 2, d: 3 }}>
              <div dangerouslySetInnerHTML={createMarkup(productClassTimelineName)} />
            </Typography>

            <Typography type="type6" fontWeight="bold" color="color100">
              <div dangerouslySetInnerHTML={createMarkup(contentDetails.name)} />
              {/* {timelinePoint.seconds} */}
            </Typography>

            {Boolean(contentDetails.teacher) && (
              <Typography type="type3" color="color80" mt={{ m: 4, d: 5 }}>
                Por: {contentDetails && contentDetails.teacher && contentDetails.teacher.name}
              </Typography>
            )}

            {!!contentDetails.description && (
              <Flex mt={{ m: 3, d: 4 }}>
                <RichContent defaultType="type2" currentTheme={currentTheme}>
                  {contentDetails.description}
                </RichContent>
              </Flex>
            )}

            {Boolean(contentDetails && contentDetails.downloads.length) && (
              <Flex mt={{ m: 3, d: 6 }}>
                <DownloadsBoxContent downloads={contentDetails.downloads} />
              </Flex>
            )}
          </Flex>
        </InteractionItem>

        {contentDetails.video.timeline.map((timeline, index) => {
          return (
            <InteractionItem
              key={`interaction-${timeline.positionSeconds}`}
              data-last={false}
              data-active={timelinePoint.seconds === timeline.positionSeconds}
              data-resolved={timelinePoint.seconds > timeline.positionSeconds}
              name={`point-${timeline.positionSeconds}`}
            >
              <Bullet data-link title={`Ir para ${timeMask(timeline.positionSeconds)}`} onClick={() => playerGoTo(timeline.positionSeconds)} />
              {/* TextOnly = 0 */}
              {timeline.videoInteractionType === 0 && (
                <Typography type="type2" color={timelinePoint.seconds === timeline.positionSeconds ? 'color100' : 'color50'} lineHeight="1.5">
                  <div dangerouslySetInnerHTML={createMarkup(timeline.timelineText)} />
                </Typography>
              )}
              {/* TextWithImage = 1 */}
              {timeline.videoInteractionType === 1 && (
                <>
                  <Typography type="type2" color={timelinePoint.seconds === timeline.positionSeconds ? 'color100' : 'color50'} lineHeight="1.5">
                    <div dangerouslySetInnerHTML={createMarkup(timeline.timelineText)} />
                  </Typography>

                  <Box>
                    <img src={timeline.imageUrl} alt={stripTags(timeline.timelineText)} />
                  </Box>
                </>
              )}

              {/* TextWithExternalUrl = 2 */}
              {timeline.videoInteractionType === 2 && (
                <Typography type="type2" color={timelinePoint.seconds === timeline.positionSeconds ? 'color100' : 'color50'} lineHeight="1.5">
                  <div dangerouslySetInnerHTML={createMarkup(timeline.timelineText)} />

                  <a href={timeline.externalUrl} target="_blank" rel="noreferrer">
                    <ButtonIcon
                      icon="openInNew"
                      label="Link para página externa"
                      iconWidth={20}
                      bg="transparent"
                      mt={2}
                      color={timelinePoint.seconds === timeline.positionSeconds ? 'primary_400' : 'color50'}
                    />
                  </a>
                </Typography>
              )}

              {/* YesNoQuestion = 3 */}

              {/* TrueFalseQuestion = 4 */}

              {/* CustomTextBinaryQuestion = 5 */}

              {/* SliderChoice = 6 */}
            </InteractionItem>
          );
        })}

        <InteractionItem data-last="true" data-active={timelinePoint.seconds === -1} name="point--1">
          <Bullet />

          <Typography type="type2" color={timelinePoint.seconds === -1 ? 'color100' : 'color50'} fontWeight="bold" mb={3}>
            E aí, gostou da aula?
          </Typography>

          <Flex pb={10}>
            <ButtonIcon
              disabled={timelinePoint.seconds !== -1}
              icon="like"
              width={120}
              height={40}
              iconWidth={22}
              borderRadius={20}
              bg={
                contentDetails.assessment.liked !== null
                  ? contentDetails.assessment.liked
                    ? 'primary_400'
                    : 'color30'
                  : timelinePoint.seconds === -1
                  ? contentLiked === null
                    ? 'primary_400'
                    : contentLiked
                    ? 'primary_400'
                    : 'color30'
                  : 'color30'
              }
              bgHover={
                contentDetails.assessment.liked !== null
                  ? contentDetails.assessment.liked
                    ? 'primary_400'
                    : 'color30'
                  : timelinePoint.seconds === -1
                  ? contentLiked === null
                    ? 'primary_200'
                    : contentLiked
                    ? 'primary_400'
                    : 'color30'
                  : 'color30'
              }
              onClick={() => setContentLiked(true)}
              title="Gostei"
            />

            <ButtonIcon
              disabled={timelinePoint.seconds !== -1}
              icon="unlike"
              width={120}
              height={40}
              iconWidth={22}
              borderRadius={20}
              ml={3}
              bg={
                contentDetails.assessment.liked !== null
                  ? contentDetails.assessment.liked
                    ? 'color30'
                    : 'primary_400'
                  : timelinePoint.seconds === -1
                  ? contentLiked === null
                    ? 'primary_400'
                    : contentLiked
                    ? 'color30'
                    : 'primary_400'
                  : 'color30'
              }
              bgHover={timelinePoint.seconds === -1 ? (contentLiked === null ? 'primary_200' : contentLiked ? 'color30' : 'primary_400') : 'color30'}
              onClick={() => setContentLiked(false)}
              title="Pode melhorar"
            />
          </Flex>
        </InteractionItem>
      </CustomElement>
    </ElementWrapper>
  );
};

export default Timeline;
